<template>
  <SidePopup
    title="Detalhes da Notificação"
    v-on:close="onClose"
    routeback="/fornecedor/marketing/push-notification/relatorio"
    :loading="loading"
    hideCancel
  >
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-2 text-muted">Carregando detalhes...</p>
    </div>

    <div v-else-if="item">
      <div class="notification-header">
        <h3 class="notification-title">{{ item.titulo }}</h3>
        <p class="notification-message">{{ item.mensagem }}</p>
      </div>

      <v-row class="info-row">
        <v-col cols="6" sm="3">
          <div class="info-item">
            <span class="info-label">Data de Envio</span>
            <span class="info-value">{{ formatDate(item.data_envio) }}</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <span class="info-label">Usuário</span>
            <span class="info-value">{{ item.usuario_nome || "-" }}</span>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <span class="info-label">Fornecedor</span>
            <span class="info-value">{{ item.fornecedor_nome || "-" }}</span>
          </div>
        </v-col>
        <v-col cols="12" v-if="item.link">
          <div class="info-item">
            <span class="info-label">Link</span>
            <a :href="item.link" target="_blank" class="link-full">
              {{ item.link }}
              <v-icon x-small class="ml-1">fas fa-external-link-alt</v-icon>
            </a>
          </div>
        </v-col>
      </v-row>

      <LineDivider class="my-3">
        <span class="section-title">
          Métricas
          <span v-if="item.metricas_updated_at" class="updated-at">
            Atualizado {{ formatDateRelative(item.metricas_updated_at) }}
          </span>
        </span>
      </LineDivider>

      <div v-if="hasMetrics" class="metrics-list">
        <div class="metric-row">
          <span class="metric-label">Entregues</span>
          <span>{{ formatNumber(item.metricas_successful) }}</span>
        </div>
        <div class="metric-row">
          <span class="metric-label">Cliques</span>
          <span>{{ formatNumber(item.metricas_converted) }}</span>
        </div>
        <div class="metric-row">
          <span class="metric-label">Falhas</span>
          <span>{{ formatNumber(item.metricas_failed) }}</span>
        </div>
      </div>

      <v-alert v-else type="info" dense outlined class="mt-2">
        Métricas ainda não disponíveis para esta notificação.
      </v-alert>

      <template v-if="hasPlatformStats">
        <LineDivider class="my-3">
          <span class="section-title">Por Canal</span>
        </LineDivider>

        <v-simple-table dense class="platform-table">
          <thead>
            <tr>
              <th>Canal</th>
              <th class="text-right">Entregues</th>
              <th class="text-right">Cliques</th>
              <th class="text-right">Falhas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="canal in canalStats" :key="canal.chave">
              <td>
                <v-icon
                  size="14"
                  :color="getCanalColor(canal.chave)"
                  class="mr-1"
                >
                  {{ getCanalIcon(canal.chave) }}
                </v-icon>
                {{ canal.rotulo }}
              </td>
              <td class="text-right font-weight-medium">
                {{ formatNumber(canal.successful) }}
              </td>
              <td class="text-right font-weight-medium">
                {{ formatNumber(canal.converted) }}
              </td>
              <td class="text-right font-weight-medium">
                {{ formatNumber(canal.failed) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </div>

    <template v-slot:buttons>
      <FormButton
        text="Atualizar Métricas"
        type="auxiliar"
        @click="refreshMetrics"
        :loading="refreshing"
      />
    </template>
  </SidePopup>
</template>

<script>
import SidePopup from "@/components/SidePopup";
import FormButton from "@/components/form/FormButton";
import LineDivider from "@/components/LineDivider";
import { mapGetters } from "vuex";
import { NOTIFICACAO_PUSH_GET_ENVIO } from "@/store/actions/notificacaoPush";
import moment from "moment";

export default {
  name: "DetalhesNotificacaoPush",
  components: {
    SidePopup,
    FormButton,
    LineDivider
  },
  data: () => ({
    loading: true,
    refreshing: false,
    item: null
  }),
  computed: {
    ...mapGetters(["getFornecedorId"]),
    hasMetrics() {
      return this.item && this.item.metricas_successful !== null;
    },
    hasPlatformStats() {
      return this.canalStats.length > 0;
    },
    // agregado por canal vindo do server (Aplicativo / Navegador)
    canalStats() {
      const porCanal = (this.item && this.item.metricas_por_canal) || null;
      if (!porCanal) return [];

      return [
        { chave: "aplicativo", rotulo: "Aplicativo", ...porCanal.aplicativo },
        { chave: "navegador", rotulo: "Navegador", ...porCanal.navegador }
      ].filter(c => c.successful || c.failed || c.errored || c.converted);
    }
  },
  mounted() {
    this.loadDetails();
  },
  methods: {
    onClose() {
      this.$emit("close");
      this.$router.push("/fornecedor/marketing/push-notification/relatorio");
    },
    async loadDetails() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          NOTIFICACAO_PUSH_GET_ENVIO,
          {
            id: this.$route.params.id,
            refreshMetrics: false
          }
        );
        this.item = response;
      } catch (error) {
        this.$vueOnToast.pop(
          "error",
          "Erro ao carregar detalhes da notificação"
        );
        this.onClose();
      } finally {
        this.loading = false;
      }
    },
    async refreshMetrics() {
      if (!this.item) return;

      this.refreshing = true;
      try {
        const response = await this.$store.dispatch(
          NOTIFICACAO_PUSH_GET_ENVIO,
          {
            id: this.item.id,
            refreshMetrics: true
          }
        );
        this.item = response;
        this.$vueOnToast.pop("success", "Métricas atualizadas");
      } catch (error) {
        this.$vueOnToast.pop("error", "Erro ao atualizar métricas");
      } finally {
        this.refreshing = false;
      }
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatDateRelative(date) {
      if (!date) return "-";
      return moment(date)
        .locale("pt-br")
        .fromNow();
    },
    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return num.toLocaleString("pt-BR");
    },
    getCanalIcon(canal) {
      return canal === "navegador" ? "fas fa-globe" : "fas fa-mobile-alt";
    },
    getCanalColor(canal) {
      return canal === "navegador" ? "teal" : "green";
    }
  }
};
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.notification-header {
  margin-bottom: 16px;
}

.notification-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
}

.notification-message {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}

.info-row {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px 0;
  margin: 0 -12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  padding: 4px 12px;
}

.info-label {
  font-size: 0.7rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.info-value {
  font-size: 0.85rem;
  color: #333;
  font-weight: 500;
}

.link-full {
  font-size: 0.85rem;
  color: var(--primary-color);
  text-decoration: none;
  word-break: break-all;
}

.link-full:hover {
  text-decoration: underline;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
}

.updated-at {
  font-weight: 400;
  font-size: 0.75rem;
  color: #888;
  margin-left: 8px;
}

.metrics-list {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-label {
  font-size: 0.85rem;
  color: #555;
}

.metric-value {
  font-size: 1rem;
  font-weight: 600;
}

.platform-table {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.platform-table th {
  font-size: 0.75rem !important;
  color: #666 !important;
  text-transform: uppercase;
  background: #fafafa !important;
}

.platform-table td {
  font-size: 0.85rem !important;
}

.text-muted {
  color: #888;
}
</style>
